import {
  Button,
  Checkbox,
  CustomRewardCurrency,
  JoonUIColor,
  Modal,
  ModalHeader,
  SPACING,
  TextButton,
  TextInput,
  Typography,
} from "@joonapp/web-shared"
import { useEffect, useState } from "react"
import { toast } from "react-hot-toast"
import { useTranslation } from "react-i18next"

import { useDeleteRewardModalStore } from "./DeleteRewardModal"
import useAddEditRewardModalStore from "./hooks/useAddEditRewardModalStore"
import useRewardsUserSelectStore from "./hooks/useRewardsUserSelectStore"
import { useScreenTimeControlsModalStore } from "./redeem/ScreenTimeControlsModal"
import { RewardEmojiPicker, RewardEmojiPickerButton } from "./RewardEmojiPicker"
import ImageCheckbox from "../../components/imageCheckbox/ImageCheckbox"
import { SelectRow } from "../../components/selectRow/SelectRow"
import { SelectRowContainer } from "../../components/selectRowContainer/SelectRowContainer"
import { ScreenTimeControlsModalStep } from "../../constants"
import {
  useAverageCoinsEarnedThisWeekQuery,
  useFamilyQuery,
} from "../../networking/queries"
import {
  useAddRewardMutation,
  useEditRewardMutation,
} from "../../networking/rewards/mutations"
import { useCustomRewardsQuery } from "../../networking/rewards/queries"
import { FamilyQuerySelect, UserSelectFilterType } from "../../types"
import { ScreenTimeControlsModalType } from "../../types/rewards"
import { ANALYTIC_EVENTS, trackAnalyticEvent } from "../../util/analytics"
import { useRouter } from "../../util/router"

const AddEditRewardModal = () => {
  const {
    isOpen,
    onClose,
    title,
    cost,
    emoji,
    rewardId,
    selectedChildren,
    currency,
    templateId,
    setTitle,
    setCost,
    setEmoji,
    toggleSelectedChild,
    setSelectedChildren,
    setCurrency,
  } = useAddEditRewardModalStore()
  const [errorText, setErrorText] = useState("")
  const { onOpen: openDeleteRewardModal } = useDeleteRewardModalStore()
  const { onOpen: onOpenScreenTimeControlsModal } =
    useScreenTimeControlsModalStore()
  const { data: rewardData } = useCustomRewardsQuery()
  const { data: averageCoinsEarned } = useAverageCoinsEarnedThisWeekQuery()
  const { data: children } = useFamilyQuery(FamilyQuerySelect.CHILDREN)
  const editRewardMutation = useEditRewardMutation()
  const addRewardMutation = useAddRewardMutation()
  const { setSelectedFilterType } = useRewardsUserSelectStore()
  const router = useRouter()
  const { t } = useTranslation()

  const isEditing = !!rewardId
  const isItem = currency === CustomRewardCurrency.ITEM
  const isDollar = currency === CustomRewardCurrency.DOLLAR
  const isMinute = currency === CustomRewardCurrency.MINUTE
  const placeholderText = isDollar
    ? t("AERM_PLACEHOLDER_DOLLAR")
    : isMinute
      ? t("AERM_PLACEHOLDER_MINUTE")
      : t("AERM_PLACEHOLDER_ITEM")
  const hasCurrencyRewards = rewardData?.some(
    (reward) =>
      reward.currency === CustomRewardCurrency.MINUTE ||
      reward.currency === CustomRewardCurrency.DOLLAR
  )

  const onClickAddReward = async () => {
    if (cost === null)
      return setErrorText(isItem ? t("AERM_ERR_COST") : t("AERM_ERR_EXCHANGE"))
    if (selectedChildren.length === 0) return setErrorText(t("AERM_ERR_CHILD"))
    if (!title) return setErrorText(t("AERM_ERR_TITLE"))
    if (!emoji) return setErrorText(t("AERM_ERR_ICON"))
    if (isEditing && !rewardId) throw new Error("Reward ID not found")
    setErrorText("")

    if (isEditing) {
      trackAnalyticEvent(ANALYTIC_EVENTS.EDIT_CUSTOM_REWARD, {
        currency: currency,
      })
      await editRewardMutation.mutateAsync({
        rewardId: rewardId as number,
        rewardInfo: {
          title: title,
          cost: cost,
          emoji: emoji,
          user_ids: selectedChildren,
          purchasable_from_joonity: true,
          currency: currency,
        },
      })
    } else {
      trackAnalyticEvent(ANALYTIC_EVENTS.CREATE_CUSTOM_REWARD, {
        currency: currency,
      })
      const rewardResponse = await addRewardMutation.mutateAsync({
        title: title,
        cost: cost,
        emoji: emoji,
        user_ids: selectedChildren,
        purchasable_from_joonity: true,
        currency: currency,
      })
      toast.success(t("AERM_TOAST_CREATED"))
      // always show Screen Time Controls modal for minute rewards
      if (currency === CustomRewardCurrency.MINUTE) {
        router.push(`/rewards/details/${rewardResponse.id}`)
        onOpenScreenTimeControlsModal({
          step: ScreenTimeControlsModalStep.INTRO,
          modalType: ScreenTimeControlsModalType.POST_CREATE,
          rewardId: rewardResponse.id,
        })
      }
      setSelectedFilterType(UserSelectFilterType.ALL_KIDS)
    }
    onClose()
  }

  const handleDeleteReward = () => {
    openDeleteRewardModal(rewardId as number)
    onClose()
  }

  // if there are no currency rewards and not a template, set the currency to minute
  useEffect(() => {
    if (children?.[0]?.user?.id && selectedChildren.length === 0)
      setSelectedChildren([children?.[0]?.user?.id])
    if (isOpen && !hasCurrencyRewards && !templateId) {
      setCurrency(CustomRewardCurrency.MINUTE)
      setCost(4)
      setTitle(t("AERM_DEFAULT_SCREEN_TIME"))
      setEmoji("📱")
    }
  }, [isOpen]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <RewardEmojiPicker setEmoji={setEmoji} />
      <Modal
        onClose={onClose}
        isOpen={isOpen}
        animate
        style={{
          background: JoonUIColor.background.xlightGray,
          width: "min(500px, 100vw)",
        }}
      >
        <ModalHeader
          title={
            isEditing
              ? t("AERM_EDIT_TITLE", { name: `${emoji} ${title}` })
              : t("AERM_CREATE_TITLE")
          }
          onClose={onClose}
          showBorderBottom
        />
        <div className="flex flex-col gap-3 py-4 px-6 pt-4 overflow-y-auto">
          <div className="flex flex-col gap-2">
            <Typography variant="bodyBold">{t("AERM_REWARD_TYPE")}</Typography>
            <div className="flex flex-row gap-2">
              <ImageCheckbox
                name="Reward type"
                key="Item or activity"
                label={t("REWARD_ITEM_OR_ACTIVITY")}
                subLabel={t("AERM_ITEM_SUBLABEL")}
                imageURL="/images/rewards_item_or_activity.png"
                onChange={() => setCurrency(CustomRewardCurrency.ITEM)}
                selected={isItem}
              />
              <ImageCheckbox
                name="Reward type"
                key="Currency exchange"
                label={t("AERM_CURRENCY_EXCHANGE")}
                subLabel={t("AERM_CURRENCY_SUBLABEL")}
                imageURL="/images/rewards_currency_exchange.png"
                onChange={() => setCurrency(CustomRewardCurrency.MINUTE)}
                selected={!isItem}
              />
            </div>
          </div>
          {!isItem && (
            <div className="flex flex-col gap-2 border border-borderPrimary rounded-md p-3 bg-white">
              <Typography variant="bodySmallBold">
                {t("AERM_WHAT_REWARD")}
              </Typography>

              <SelectRowContainer>
                <SelectRow
                  hideBorder
                  showBorderBottom
                  onClick={() => setCurrency(CustomRewardCurrency.MINUTE)}
                >
                  <div className="flex flex-row gap-2 items-center">
                    <img
                      src="/images/icons/coin_to_screen_time.png"
                      alt="Screen time"
                      className="h-8 w-auto"
                    />
                    <div className="flex flex-col text-left">
                      <Typography variant="bodySmallBold">
                        {t("AERM_SCREEN_TIME")}
                      </Typography>
                      <Typography
                        variant="bodyXSmall"
                        color={JoonUIColor.text.secondary}
                      >
                        {t("AERM_SCREEN_TIME_DESC")}
                      </Typography>
                    </div>
                  </div>
                  <Checkbox
                    name="Screen time"
                    selected={isMinute}
                    onChange={() => setCurrency(CustomRewardCurrency.MINUTE)}
                    hideBorder
                    inputType="radio"
                  />
                </SelectRow>
                <SelectRow
                  hideBorder
                  onClick={() => setCurrency(CustomRewardCurrency.DOLLAR)}
                >
                  <div className="flex flex-row gap-2 items-center">
                    <img
                      src="/images/icons/coin_to_money.png"
                      alt="Allowance"
                      className="h-8 w-auto"
                    />
                    <div className="flex flex-col text-left">
                      <Typography variant="bodySmallBold">
                        {t("AERM_ALLOWANCE")}
                      </Typography>
                      <Typography
                        variant="bodyXSmall"
                        color={JoonUIColor.text.secondary}
                      >
                        {t("AERM_ALLOWANCE_DESC")}
                      </Typography>
                    </div>
                  </div>
                  <Checkbox
                    name="Allowance"
                    selected={isDollar}
                    onChange={() => setCurrency(CustomRewardCurrency.DOLLAR)}
                    hideBorder
                    inputType="radio"
                  />
                </SelectRow>
              </SelectRowContainer>
            </div>
          )}

          <div className="flex flex-col gap-2 border border-borderPrimary rounded-md p-3 bg-white">
            <Typography variant="bodySmallBold">
              {isItem ? t("AERM_SET_ITEM_PRICE") : t("AERM_SET_EXCHANGE_RATE")}
            </Typography>

            <Typography color={JoonUIColor.text.secondary} variant="bodySmall">
              {t("AERM_COINS_INFO", { coins: averageCoinsEarned })}
            </Typography>
            <div className="flex flex-row justify-start items-center gap-2">
              <Typography variant="bodySmall">
                {isItem ? t("AERM_COST_LABEL") : t("AERM_EXCHANGE_LABEL")}
              </Typography>
              <div style={{ maxWidth: "100px" }}>
                <TextInput
                  name="Cost"
                  value={cost !== null ? `${cost}` : ""}
                  placeholder={isMinute ? "5" : "100"}
                  maxLength={6}
                  onChange={(e) => {
                    const value = e.target.value
                    if (value === "") return setCost(null)
                    if (isNaN(Number(value)) || Number(value) < 0) return
                    setCost(Number(value))
                  }}
                  style={{
                    marginBottom: SPACING.space1,
                    width: "90px",
                    height: "40px",
                    borderRadius: SPACING.space2,
                    border: `1px solid ${JoonUIColor.border.default}`,
                  }}
                />
              </div>
              <Typography
                variant="bodySmall"
                style={{ whiteSpace: "nowrap", width: "fit-content" }}
              >
                {cost === 1 ? t("AERM_COIN_ONE") : t("AERM_COIN_OTHER")}
                {!isItem &&
                  ` ${isMinute ? t("AERM_FOR_EACH_MINUTE") : t("AERM_FOR_EACH_DOLLAR")}`}
              </Typography>
            </div>
          </div>

          <div className="flex flex-col gap-2 border border-borderPrimary rounded-md p-3 bg-white">
            <Typography variant="bodySmallBold">{t("AERM_WHO_FOR")}</Typography>
            <SelectRowContainer>
              {children?.map((child, index) => (
                <SelectRow
                  key={child.user.id}
                  hideBorder
                  showBorderBottom={index !== children.length - 1}
                >
                  <Typography variant="bodySmall">{child.user.name}</Typography>
                  <Checkbox
                    name={child.user.name}
                    key={child.user.id}
                    onChange={() => toggleSelectedChild(child.user.id)}
                    selected={selectedChildren.includes(child.user.id)}
                    hideBorder
                  />
                </SelectRow>
              ))}
            </SelectRowContainer>
          </div>

          <div className="flex flex-col gap-2 border border-borderPrimary rounded-md p-3 bg-white">
            <Typography variant="bodySmallBold">
              {t("AERM_REWARD_DETAILS")}
            </Typography>
            {!isItem && (
              <Typography
                variant="bodySmall"
                color={JoonUIColor.text.secondary}
              >
                {isMinute ? t("AERM_MINUTE_HINT") : t("AERM_DOLLAR_HINT")}
              </Typography>
            )}
            <TextInput
              name="Reward Title"
              maxLength={100}
              value={title}
              placeholder={placeholderText}
              onChange={(e) => setTitle(e.target.value)}
              fullWidth
            />
            <RewardEmojiPickerButton emoji={emoji} />
          </div>
        </div>
        <div className="flex flex-col gap-2 px-6 pb-4 pt-2 border-t border-borderPrimary">
          <Typography
            variant="bodySmall"
            color={JoonUIColor.semantic.alert}
            className="text-center w-full my-1"
          >
            {errorText}
          </Typography>

          <div className="flex flex-row justify-between items-center w-full gap-2">
            {isEditing && (
              <TextButton onClick={handleDeleteReward}>
                <Typography
                  variant="bodySmallBold"
                  color={JoonUIColor.semantic.alert}
                >
                  {t("QB_DELETE")}
                </Typography>
              </TextButton>
            )}
            <Button
              text={isEditing ? t("AERM_SAVE_CHANGES") : t("AERM_ADD_REWARD")}
              isLoading={
                addRewardMutation.isLoading || editRewardMutation.isLoading
              }
              onClick={onClickAddReward}
              fullWidth
            />
          </div>
        </div>
      </Modal>
    </>
  )
}

export default AddEditRewardModal
